import React, { FC } from 'react';

import { Link } from 'react-router-dom';
import toLower from 'lodash/toLower';

import RoutingUtils from '../utils/utils-routing';
import {
    PRODUCT_CLASS_LANGUAGE_LEARNING,
    PRODUCT_CLASS_LOTE,
    PRODUCT_CLASS_STANDARD,
    ProductClass
} from '../../store/constants';
import MetadataUtils from '../utils/utils-metadata';
import { ProductMetaData } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { useTranslation } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';

type Props = {
    readonly product: ProductMetaData;
    readonly withoutLink?: boolean;
    readonly loanFormatKey?: string | null;
    readonly shopLanguage: string | undefined;
};

export const ProductLanguage: FC<Props> = ({
    product: { language: productLanguage, productClass },
    withoutLink = false,
    loanFormatKey,
    shopLanguage
}) => {
    const language = useLanguage();

    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    if (!language || !productLanguage) {
        return null;
    }

    let translatedLanguage = t(`filters:language.${productLanguage}`, productLanguage);

    if (withoutLink) {
        return <p>{translatedLanguage}</p>;
    }

    const linkTo = createLanguageLink({
        productClass,
        pathLanguage: language,
        filterLanguage: productLanguage,
        loanFormatKey,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory: ageCategorySearchParam
    });

    return <p>{linkTo ? <Link to={linkTo}>{translatedLanguage}</Link> : <span>{translatedLanguage}</span>}</p>;
};

export default ProductLanguage;

interface LanguageLinkParams {
    productClass: ProductClass | undefined;
    pathLanguage: string;
    filterLanguage: string;
    loanFormatKey: string | null | undefined;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}
function createLanguageLink({
    productClass,
    pathLanguage,
    shopLanguage,
    isMultiLanguageShopEnabled,
    filterLanguage,
    loanFormatKey,
    ageCategory
}: LanguageLinkParams) {
    const isCatalogAllProductsLink = productClass === PRODUCT_CLASS_STANDARD;
    const isLoteProductLink = productClass === PRODUCT_CLASS_LOTE;
    const isLanguageLearningProductLink = productClass === PRODUCT_CLASS_LANGUAGE_LEARNING;

    if (isCatalogAllProductsLink || (isLoteProductLink && isMultiLanguageShopEnabled)) {
        return RoutingUtils.catalogueLocationDescriptorString({
            loanFormat: loanFormatKey,
            filter: { language: { include: [filterLanguage] } },
            shopLanguage,
            isMultiLanguageShopEnabled,
            ageCategory
        });
    }

    if (isLoteProductLink && !isMultiLanguageShopEnabled) {
        const lowerLoanFormat = toLower(loanFormatKey ?? 'all');
        // PORTAL-3993 use all products link instead of featured link
        return RoutingUtils.foreignLanguageAllProductsPageLocationDescriptor({
            languageId: filterLanguage,
            loanFormat: lowerLoanFormat,
            ageCategory
        });
    }

    if (isLanguageLearningProductLink) {
        return MetadataUtils.createMetaDataLink({
            productClass,
            filter: { language: { include: [filterLanguage] } },
            extra: { loanFormatKey, language: pathLanguage },
            shopLanguage,
            isMultiLanguageShopEnabled,
            ageCategory
        });
    }

    return undefined;
}
